import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ChevronDown, LucideIconData } from 'lucide-angular';
import { catchError, filter, map, Observable, of, startWith, switchMap, take } from 'rxjs';

import { AuthModalService } from '../../../../shared/services/auth-modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import {
  PLAYER_PROVINCES,
  PLAYERS_CONTACT_PATH,
  PLAYERS_PROFILE_PATH,
} from '../../../../shared/players/players.constants';
import { PLAYERS_COPY } from '../../../../shared/players/players.copy';
import type {
  OwnPlayerProfile,
  PlayersDirectoryResponse,
} from '../../../../shared/players/players.model';
import { PlayersResendVerificationComponent } from '../../../../shared/players/players-resend-verification.component';
import { playersSessionIdentity } from '../../../../shared/players/players-session';
import { PlayersService } from '../../../../shared/players/players.service';
import { isVerifiedAccount, serverMessage } from '../../../../shared/players/players.utils';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { PaginationComponent } from '../../../../shared/ui/pagination/pagination.component';
import type { PublicGameSectionSummary } from '../../../game-section/data/game-section.model';
import { GameSectionService } from '../../../game-section/data/game-section.service';
import type { PlayerCard } from '../../../../shared/players/players.model';
import { PlayerCardComponent } from '../player-card/player-card.component';
import {
  PlayRequestModalComponent,
  type PlayRequestSender,
} from '../play-request-modal/play-request-modal.component';

/** Estado visible de la sección (D3 del spec web). */
export type PlayerFinderStatus = 'pending' | 'guest' | 'unverified' | 'ready' | 'error';

/** Lo que se sabe de la sesión antes de preguntar a la API. */
type SessionStatus = 'pending' | 'guest' | 'unverified' | 'verified';

/** Denegaciones que la API puede dar con sesión: 401 (caducada) y 403 (sin verificar). */
type Denied = { kind: 'expired' } | { kind: 'forbidden'; message: string | null };

type DirectoryState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; response: PlayersDirectoryResponse }
  | { kind: 'error' }
  | Denied;

type MeState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; profile: OwnPlayerProfile | null }
  | { kind: 'failed' }
  | { kind: 'expired' };

type Banner = 'none' | 'no-profile' | 'hidden' | 'suspended';

const SKELETON_CARDS = [1, 2, 3, 4, 5, 6] as const;

/**
 * «¿Con quién jugar?» en `/comunidad`: el directorio real de jugadores (B11 J1).
 *
 * **Máquina de estados (D3)**: `pending` → skeleton · `guest` · `unverified` · `ready` ·
 * `error`. El SSR y el primer render del cliente pintan el mismo skeleton: la sesión
 * solo existe en el navegador y el `APP_INITIALIZER` la trae ya resuelta al hidratar,
 * así que el estado real espera a `afterNextRender` (N3). Sin verificar no se llama a la
 * API (B30); 401 → invitado con aviso de sesión caducada (local: la cabecera no se
 * entera, P1); 403 → sin verificar con el texto del servidor.
 *
 * **Carreras**: filtros, página, usuario y reintento forman la clave de la petición y
 * `switchMap` cancela la anterior; una respuesta tardía nunca pinta la rejilla. Sin
 * sesión la clave es `null` y el estado vuelve a vacío: el logout no deja jugadores en
 * memoria (ni el perfil propio del banner).
 */
@Component({
  selector: 'app-player-finder',
  imports: [
    ButtonComponent,
    IconComponent,
    NgTemplateOutlet,
    PaginationComponent,
    PlayerCardComponent,
    PlayersResendVerificationComponent,
    PlayRequestModalComponent,
    RouterLink,
  ],
  templateUrl: './player-finder.component.html',
  styleUrl: './player-finder.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class PlayerFinderComponent {
  private readonly auth = inject(AuthService);
  private readonly authModal = inject(AuthModalService);
  private readonly players = inject(PlayersService);
  private readonly gameSections = inject(GameSectionService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly copy = PLAYERS_COPY.directory;
  protected readonly bannerCopy = PLAYERS_COPY.banner;
  protected readonly provinces = PLAYER_PROVINCES;
  protected readonly profilePath = PLAYERS_PROFILE_PATH;
  protected readonly contactPath = PLAYERS_CONTACT_PATH;
  protected readonly skeletonCards = SKELETON_CARDS;
  protected readonly chevronDownIcon: LucideIconData = ChevronDown;

  /** Solo pasa a `true` en el navegador, tras el primer render (hidratación incluida). */
  private readonly hydrated = signal(false);

  /** `null` = «Todos» / «Todas» (no se envía, D5). */
  protected readonly game = signal<string | null>(null);
  protected readonly province = signal<string | null>(null);
  protected readonly page = signal(1);
  private readonly retryTick = signal(0);

  protected readonly games = signal<readonly PublicGameSectionSummary[]>([]);

  private readonly directory = signal<DirectoryState>({ kind: 'idle' });

  /** Tarjeta con el modal «Invitar a jugar» abierto (D32); `null` = cerrado. */
  protected readonly inviteTarget = signal<PlayerCard | null>(null);
  /** Aviso del último envío (texto del servidor, `REQUEST_SENT`). */
  protected readonly sentNotice = signal<string | null>(null);
  /** Tras el 201 el botón que abrió el modal ya no existe (la tarjeta pasa a `sent`): el foco va al aviso. */
  private readonly sentNoticeRef = viewChild<ElementRef<HTMLElement>>('sentNotice');
  private readonly injector = inject(Injector);
  private readonly me = signal<MeState>({ kind: 'idle' });

  /** Un flag por corgi (D28 a): si falla la imagen del vacío, la del error sigue. */
  protected readonly emptyMascotError = signal(false);
  protected readonly errorMascotError = signal(false);

  private readonly session = computed<SessionStatus>(() => {
    if (!this.hydrated() || this.auth.isLoading()) return 'pending';
    const user = this.auth.currentUser();
    if (!user) return 'guest';
    return isVerifiedAccount(user) ? 'verified' : 'unverified';
  });

  protected readonly status = computed<PlayerFinderStatus>(() => {
    const session = this.session();
    if (session !== 'verified') return session;
    const directory = this.directory();
    if (directory.kind === 'expired' || this.me().kind === 'expired') return 'guest';
    if (directory.kind === 'forbidden') return 'unverified';
    if (directory.kind === 'error') return 'error';
    return 'ready';
  });

  /** Solo tras un 401 con sesión: el aviso va encima de los botones de invitado. */
  protected readonly sessionExpired = computed(
    () =>
      this.session() === 'verified' &&
      (this.directory().kind === 'expired' || this.me().kind === 'expired'),
  );

  /** Texto del 403 si el servidor lo mandó; si no, el local (= `NOT_VERIFIED_DIRECTORY`). */
  protected readonly unverifiedText = computed(() => {
    const directory = this.directory();
    return (directory.kind === 'forbidden' && directory.message) || this.copy.notVerifiedFallback;
  });

  protected readonly loading = computed(() => {
    const kind = this.directory().kind;
    return kind === 'loading' || kind === 'idle';
  });

  protected readonly cards = computed(() => {
    const directory = this.directory();
    return directory.kind === 'loaded' ? directory.response.data : [];
  });

  protected readonly meta = computed(() => {
    const directory = this.directory();
    return directory.kind === 'loaded' ? directory.response.meta : null;
  });

  /** Perfil propio conocido (`undefined` = aún no se sabe o `/players/me` falló). */
  private readonly ownProfile = computed<OwnPlayerProfile | null | undefined>(() => {
    const me = this.me();
    return me.kind === 'loaded' ? me.profile : undefined;
  });

  protected readonly banner = computed<Banner>(() => {
    const profile = this.ownProfile();
    if (profile === undefined) return 'none';
    if (profile === null) return 'no-profile';
    if (profile.suspended || profile.hidden_reason === 'suspended') return 'suspended';
    return profile.visible ? 'none' : 'hidden';
  });

  /** «Sé el primero»: solo sin perfil o con él oculto (y nunca a un suspendido). */
  protected readonly showEmptyCta = computed(() => {
    const banner = this.banner();
    return banner === 'no-profile' || banner === 'hidden';
  });

  protected readonly emptyText = computed(() => {
    const game = this.game();
    const province = this.province();
    const gameName = game ? (this.games().find((g) => g.slug === game)?.name ?? game) : null;
    const provinceName = province
      ? (this.provinces.find((p) => p.slug === province)?.name ?? province)
      : null;
    if (gameName && provinceName) return this.copy.emptyBoth(gameName, provinceName);
    if (gameName) return this.copy.emptyGame(gameName);
    if (provinceName) return this.copy.emptyProvince(provinceName);
    return this.copy.emptyAll;
  });

  constructor() {
    // `afterNextRender` ya no corre en el SSR real (`ngServerMode`), pero la guarda de
    // plataforma lo hace explícito y vale también con `PLATFORM_ID = 'server'` (tests).
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      afterNextRender(() => this.hydrated.set(true));
    }

    const verifiedUser = computed(() => (this.session() === 'verified' ? this.auth.currentUser() : null));

    // Directorio: la clave cambia con usuario, filtros, página y reintento.
    const directoryKey = computed(() => {
      const user = verifiedUser();
      if (!user) return null;
      return {
        user,
        query: { game: this.game(), province: this.province(), page: this.page() },
        tick: this.retryTick(),
      };
    });
    toObservable(directoryKey)
      .pipe(
        switchMap((key): Observable<DirectoryState> => {
          if (!key) return of({ kind: 'idle' });
          return this.players.getDirectory(key.query).pipe(
            map((response): DirectoryState => ({ kind: 'loaded', response })),
            catchError((err: unknown) => of(this.directoryFailure(err))),
            startWith<DirectoryState>({ kind: 'loading' }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => this.directory.set(state));

    // Perfil propio (D8), en paralelo; no depende de los filtros.
    const meKey = computed(() => {
      const user = verifiedUser();
      return user ? { user, tick: this.retryTick() } : null;
    });
    toObservable(meKey)
      .pipe(
        switchMap((key): Observable<MeState> => {
          if (!key) return of({ kind: 'idle' });
          return this.players.getMe().pipe(
            map((profile): MeState => ({ kind: 'loaded', profile })),
            catchError((err: unknown) =>
              of<MeState>(
                err instanceof HttpErrorResponse && err.status === 401
                  ? { kind: 'expired' }
                  : { kind: 'failed' },
              ),
            ),
            startWith<MeState>({ kind: 'loading' }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((state) => this.me.set(state));

    // Juegos del filtro: la primera vez que hay sesión verificada. Si fallan, solo «Todos».
    toObservable(this.session)
      .pipe(
        filter((session) => session === 'verified'),
        take(1),
        switchMap(() =>
          this.gameSections.getGames().pipe(catchError(() => of<PublicGameSectionSummary[]>([]))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe((games) => this.games.set(games));

    // El modal se ata a la sesión (H1; Astra J3 H3/H4): con cualquier cambio de identidad,
    // también a pendiente o A → B → A, se cierra (y su envío en vuelo se cancela) y el aviso se va.
    playersSessionIdentity()
      .changes$.pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.inviteTarget.set(null);
        this.sentNotice.set(null);
      });
  }

  /**
   * Si quien mira puede enviar (B21): perfil propio con consentimiento y sin suspender. Si
   * `/players/me` aún no llegó o falló, decide la API (el modal muestra el formulario).
   */
  protected readonly sender = computed<PlayRequestSender>(() => {
    const profile = this.ownProfile();
    if (profile === undefined) return 'can-send';
    if (profile === null || !profile.consented) return 'need-profile';
    if (profile.suspended || profile.hidden_reason === 'suspended') return 'suspended';
    return 'can-send';
  });

  protected openInvite(card: PlayerCard): void {
    this.sentNotice.set(null);
    this.inviteTarget.set(card);
  }

  protected closeInvite(): void {
    this.inviteTarget.set(null);
  }

  /** 201: la tarjeta pasa a `sent` en local, sin recargar el directorio (D32). */
  protected onInviteSent(event: { alias: string; message: string }): void {
    this.directory.update((state) =>
      state.kind === 'loaded'
        ? {
            ...state,
            response: {
              ...state.response,
              data: state.response.data.map((card) =>
                card.alias === event.alias ? { ...card, request_state: 'sent' as const } : card,
              ),
            },
          }
        : state,
    );
    this.inviteTarget.set(null);
    this.sentNotice.set(event.message);
    afterNextRender(() => this.sentNoticeRef()?.nativeElement.focus(), { injector: this.injector });
  }

  /** 404: ese jugador ya no está; al cerrar el modal se recarga el directorio. */
  protected onInviteNotFound(): void {
    this.retry();
  }

  protected selectGame(slug: string | null): void {
    this.sentNotice.set(null);
    this.game.set(slug);
    this.page.set(1);
  }

  protected onProvinceChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.sentNotice.set(null);
    this.province.set(value === '' ? null : value);
    this.page.set(1);
  }

  protected goToPage(page: number): void {
    this.sentNotice.set(null);
    this.page.set(page);
    this.host.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected retry(): void {
    this.sentNotice.set(null);
    this.retryTick.update((tick) => tick + 1);
  }

  protected openLogin(): void {
    this.authModal.open('login');
  }

  protected openRegister(): void {
    this.authModal.open('register');
  }

  protected onEmptyMascotError(): void {
    this.emptyMascotError.set(true);
  }

  protected onErrorMascotError(): void {
    this.errorMascotError.set(true);
  }

  private directoryFailure(err: unknown): DirectoryState {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 401) return { kind: 'expired' };
      if (err.status === 403) return { kind: 'forbidden', message: serverMessage(err) };
    }
    return { kind: 'error' };
  }
}
